import { AxiosStatic } from "axios";
import { AiClient, AiClientResult, AiOptions, AiTool } from "@me8eon/aiclient";
import { ToolCall, Message, isAssistantMessageRequest, normaliseMessage } from "@me8eon/messages";
import { JwtTokenFn } from "@me8eon/authentication";
import { ErrorsOr, LogAnd } from "@me8eon/errors";
import { AzureChatCompletionsResponse } from "./azure.ai.domain";
import { EnvironmentConfig, LLM_MODEL } from "@me8eon/config";

export type AzureAiConfig = {
    axios: AxiosStatic;
    baseURL: string;
    deploymentId: string;
    apiVersion: string;
    apiKey: JwtTokenFn;
    customisation?: any;
    debug?: boolean;
};

export function defaultAzureAiConfig(axios: AxiosStatic, baseURL: string, apikey: JwtTokenFn): AzureAiConfig {
    return {
        baseURL,
        axios,
        deploymentId: LLM_MODEL,
        apiVersion: "2024-02-15-preview",
        apiKey: apikey,
    };
}

export function aiToolToBody(tool: AiTool) {
    return {
        type: "function",
        function: {
            name: tool.name,
            description: tool.description,
            parameters: tool.parameters,
            required: tool.required,
        },
    };
}

export function normaliseMessageForAzureAi(message: Message): any {
    if (isAssistantMessageRequest(message)) {
        return {
            role: "assistant",
            content: "",
            tool_calls: message.toolCalls.map(tc => ({
                id: tc.tool_call_id,
                type: "function",
                function: {
                    name: tc.tool_name,
                    arguments: JSON.stringify(tc.tool_args),
                },
            })),
        };
    } else {
        return normaliseMessage(message);
    }
}

export function normaliseMessagesForAzureAi(messages: Message[]): any[] {
    return messages.map(normaliseMessageForAzureAi);
}

function optionsToBody(options: AiOptions, config: AzureAiConfig, messages: Message[]): any {
    const customisation = options.customisation || config.customisation || {};
    const temperature = options.temperature !== undefined ? options.temperature : 0.7;
    const tools = (options.tools || undefined)?.map(aiToolToBody);
    const responseFormat = options.response_format ? { response_format: options.response_format } : {};
    return { model: LLM_MODEL, ...customisation, temperature, messages: normaliseMessagesForAzureAi(messages), tools, ...responseFormat };
}

export const azureOpenAiClient = (config: AzureAiConfig): AiClient => {
    const { axios, baseURL, apiVersion, apiKey, debug } = config;
    const axiosInstance = axios.create({ baseURL });

    return async (messages: Message[], options: AiOptions = {}): Promise<LogAnd<ErrorsOr<AiClientResult>>> => {
        // backend-node-v2's /service/completions proxy injects the AI gateway credential
        // server-side, so only our own app JWT needs to travel with the request.
        const headers = {
            "Authorization": `Bearer ${await apiKey()}`,
            "Content-Type": "application/json",
        };

        if (debug) console.log("azureOpenAiClient", messages, config);

        const path = `${EnvironmentConfig.nodeService}/service/completions`;
        const body = optionsToBody(options, config, messages);

        try {
            const response = await axiosInstance.post<AzureChatCompletionsResponse>(path, body, { headers });
            const choice = response.data.choices[0];
            const lastMessage = choice.message;

            const toolCalls: ToolCall[] | undefined = lastMessage.tool_calls?.map(tc => ({
                tool_call_id: tc.id,
                tool_name: tc.function.name,
                tool_args: JSON.parse(tc.function.arguments),
            }));

            const msg: Message = toolCalls ? { role: "assistant", toolCalls, content: "" } : choice.message;
            return { value: { message: msg, toolCalls }, log: "azure.openai.call.success" };
        } catch (error) {
            console.error(error);
            const errMsg = (error as any)?.response?.data?.error?.message || (error as any)?.message || "Unknown error";

            await options.stream?.sink?.({
                type: "error",
                error: errMsg
            });

            return {
                errors: [errMsg],
                log: { whatHappened: "azure.openai.call.failed", severity: "error", params: { error: String(error) } },
            };
        }
    };
};